<template>
    <div class="container">
        <div class="row">
            <div class="col-12">
                <h1 class="title pb-lg-4 mb-5">{{ pageTitle }}</h1>

                <h2 class="header pb-3 pt-lg-3 mb-3 mt-5">
                    PLEASE ENTER YOUR VERIFICATION CODE
                </h2>

                <div v-if="errorNotification">
                    <ErrorMessage class="mb-4 mb-lg-5" :errorNotification="errorNotification" />
                </div>

                <div v-if="successNotification">
                    <SuccessMessage class="mb-4 mb-lg-5" :notification="successNotification" />
                </div>

                <p class="text" v-if="otp.value.verificationMethod === 'email'">
                    Please enter your one time code that we sent via email to your registered email address
                </p>
                <p class="text" v-if="otp.value.verificationMethod === 'sms'">
                    Please enter your one time code that we sent via SMS to {{ getPhoneNumber() }}
                </p>

                <label class="label pt-3 pt-lg-0 mt-5">Verification Code</label>
                <input class="form-control input-field pl-4"
                       inputmode="numeric"
                       pattern="[0-9]*"
                       autocomplete="one-time-code"
                       v-model.trim.lazy="verificationCode"
                       :class="{
            'error-box':
              v$.verificationCode.$dirty &&
              (!v$.verificationCode.required ||
                !v$.verificationCode.maxLength ||
                !v$.verificationCode.minLength),
          }" />
                <div class="error-text mt-1"
                     v-if="
            v$.verificationCode.$dirty &&
            (!v$.verificationCode.required ||
              !v$.verificationCode.maxLength ||
              !v$.verificationCode.minLength)
          ">
                    <p>Please enter the verification code</p>
                </div>

                <button @click="verifyOTP" type="submit" class="btn btn-primary green-btn mt-4">
                    <b-spinner v-if="showLoader" label="Spinning" class="white-spinner"></b-spinner>
                    <div v-if="!showLoader">Verify</div>
                </button>

                <h2 class="header pb-3 pt-lg-3 mb-3 mt-5">
                    HAVEN'T RECEIVED A CODE?
                </h2>
                <p class="text">
                    <span class="hyperlink"
                          v-if="otp.value.verificationMethod === 'sms'"
                          @click="resendOTP">Resend SMS Text Message</span>
                    <span v-if="otp.value.verificationMethod === 'email'">
                        <a href="#" @click.prevent="resendOTP">Resend via email</a>
                        <br />(Make sure to also check your spam folder)
                    </span>
                </p>
                <b-spinner v-if="showResendLoader" label="Spinning" class="gold-spinner"></b-spinner>
                <p class="text mt-3">
                    If you're still unable to verify your
                    <span v-if="otp.value.verificationMethod === 'email'">email address</span>
                    <span v-else>mobile number</span>
                    please contact the Customer Service Team on +44 (0)20 7730 1234
                </p>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { ref, computed, onMounted } from 'vue'
    import { useStore } from 'vuex'
    import { useRouter } from 'vue-router'
    import useVuelidate from '@vuelidate/core'
    import { required, maxLength, minLength } from '@vuelidate/validators'
    import ErrorMessage from '@/components/ErrorMessage.vue'
    import SuccessMessage from '@/components/SuccessMessage.vue'
    import { verifyOTP as verifyOTPApi, sendOTP } from '@/services/2FAClient.js'
    import digitalApi from '@/services/DigitalApiClient.js'

    // Props
    //const props = defineProps({
    //    verificationMethods: {
    //        type: Array,
    //        default: () => []
    //    }
    //})

    // State
    const pageTitle = 'Verify your code'
    const showLoader = ref(false)
    const showResendLoader = ref(false)
    const verificationCode = ref('')
    const errorNotification = ref(null)
    const successNotification = ref(null)

    // Vuex
    const store = useStore()
    const router = useRouter()
    const otp = computed(() => store.state.otp)
    const isMyBeauty = computed(() => window.$isMyBeauty) // or inject if available

    // Vuelidate
    const rules = {
        verificationCode: {
            required,
            minLength: minLength(5),
            maxLength: maxLength(5)
        }
    }
    const v$ = useVuelidate(rules, { verificationCode })

    // Lifecycle
    onMounted(() => {
        if (!otp.value.verificationMethods) {
            router.push('/')
        }
    })

    // Methods
    function getPhoneNumber() {
        if (otp.value.verificationMethods) {
            const sms = otp.value.verificationMethods.find(x => x.type === 'sms')
            return sms ? sms.value : ''
        }
        return ''
    }

    function resendOTP() {
        showResendLoader.value = true
        errorNotification.value = null
        successNotification.value = null
        sendOTP(otp.value.verificationMethod)
            .then(response => {
                store.dispatch('saveTokenRef', {
                    tokenRef: response.data.otpRef,
                    verificationMethod: otp.value.verificationMethod
                })
                successNotification.value = `We have sent another verification code to your ${otp.value.verificationMethod === 'email' ? 'email address' : 'mobile number'
                    }. Please enter the new code below.`
            })
            .catch(error => {
                if (error.response && (error.response.status === 400 || error.response.status === 401)) {
                    store.dispatch('reset')
                    router.push('/')
                } else {
                    errorNotification.value = 'We’re sorry that didn’t work. Please try again.'
                }
            })
            .finally(() => (showResendLoader.value = false))
    }

    function verifyOTP() {
        errorNotification.value = null
        successNotification.value = null
        v$.value.$touch()
        if (!v$.value.$invalid) {
            showLoader.value = true
            verifyOTPApi(otp.value.tokenRef, verificationCode.value)
                .then(async () => {
                    let resultGetUser = await digitalApi.getUser()
                    if (isMyBeauty.value && !resultGetUser.isMyBeautyMember) {
                        router.push({
                            name: 'JoinMyBeauty',
                            params: { user: resultGetUser }
                        })
                    } else {
                        router.push({ name: 'DownloadAfterSignIn' })
                    }
                })
                .catch(error => {
                    if (error.response && error.response.status === 429) {
                        errorNotification.value =
                            "You've exceeded the maximum number of attempts. Please try again later."
                    } else if (
                        error.response &&
                        (error.response.status === 400 ||
                            error.response.status === 401 ||
                            error.response.status === 404)
                    ) {
                        errorNotification.value =
                            'The verification code is invalid or has expired. Please try again or re-send the code using the link below.'
                    } else {
                        errorNotification.value = 'We’re sorry that didn’t work. Please try again.'
                    }
                })
                .finally(() => (showLoader.value = false))
        }
    }
</script>

<style lang="scss" scoped>
    .input-btn {
        float: left;

        &:hover,
        :active,
        :focus {
            cursor: pointer;
        }
    }

    .clickable {
        &:hover,
        :active,
        :focus {
            cursor: pointer;
        }
    }
</style>
